import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../common/components/Icon/Icon";
import { trackEvent } from "../../../trackEvent";
import type { DownloadStep } from "../utils/zipExtractor";
import { downloadAndExtractApk } from "../utils/zipExtractor";

interface Props {
  url: string;
  fileName: string;
}

const STEPS: DownloadStep[] = ["downloading", "extracting", "saving"];

const STEP_LABEL_KEYS: Record<DownloadStep, string> = {
  downloading: "apkDownload.downloading",
  extracting: "apkDownload.extracting",
  saving: "apkDownload.saving",
};

const STEP_ICONS: Record<DownloadStep, JSX.Element> = {
  downloading: (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  ),
  extracting: (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 7h8M8 12h8m-4-9v18M5 3h14l-2 18H7L5 3z"
      />
    </svg>
  ),
  saving: (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
};

/**
 * Download button that fetches a build URL, extracts the APK from the ZIP
 * if applicable, and triggers a browser download of the `.apk` file.
 * Shows a 3-step progress indicator: Download ZIP → Extract APK → Save.
 * Supports cancellation via AbortController.
 */
const ApkDownloadButton = ({ url, fileName }: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [step, setStep] = useState<DownloadStep>("downloading");
  const abortRef = useRef<AbortController | null>(null);

  const handleClick = async () => {
    if (status === "busy") return;
    trackEvent("apk-download-hub");
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus("busy");
    setStep("downloading");
    try {
      await downloadAndExtractApk(url, fileName, setStep, controller.signal);
      setStatus("done");
      setTimeout(() => setStatus("idle"), 2000);
    } catch {
      if (controller.signal.aborted) {
        setStatus("idle");
      } else {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 3000);
      }
    } finally {
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
  };

  if (status === "idle") {
    return (
      <button onClick={handleClick} title={fileName}>
        <Icon icon="DownloadIcon" className="" width={24} stroke="rgb(0 200 137)" />
      </button>
    );
  }

  if (status === "done") {
    return (
      <div className="flex items-center gap-1.5">
        <svg
          className="w-5 h-5 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-xs text-green-500">{t("apkDownload.done")}</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center gap-1.5">
        <Icon icon="ExclamationIcon" className="stroke-red-400" width={20} />
        <span className="text-xs text-red-400">{t("apkDownload.error")}</span>
      </div>
    );
  }

  // busy — 3-step progress
  const currentIdx = STEPS.indexOf(step);

  return (
    <div className="flex flex-col gap-1.5 min-w-[140px]">
      {/* Step indicators */}
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => {
          const isDone = i < currentIdx;
          const isCurrent = i === currentIdx;
          return (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                  isDone
                    ? "bg-green-500 text-white"
                    : isCurrent
                    ? "bg-green-100 text-green-600 ring-2 ring-green-400"
                    : "bg-gray-100 text-gray-300"
                }`}
              >
                {isDone ? (
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  STEP_ICONS[s]
                )}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 rounded transition-colors ${
                    isDone ? "bg-green-500" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
      {/* Current step label + cancel */}
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-3 border-[1.5px] border-green-500 border-t-transparent rounded-full animate-spin shrink-0" />
        <span className="text-[11px] text-gray-500 flex-1">{t(STEP_LABEL_KEYS[step])}</span>
        <button
          onClick={handleCancel}
          className="text-[10px] text-gray-400 hover:text-red-500 transition-colors"
          title={t("apkDownload.cancel")}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ApkDownloadButton;
